import { RiSearchLine } from '@remixicon/react';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

import { useGlobalSearchEnabled } from '../hooks/useGlobalSearchEnabled';
import { usePersistedSearchQuery } from '../hooks/usePersistedSearchQuery';
import { CommandPalette } from './CommandPalette';
import {
  SEARCH_BAR_LAYOUT_ID,
  SEARCH_BAR_NO_MOTION,
  SEARCH_BAR_TRANSITION,
  SEARCH_ICON_LAYOUT_ID,
} from './searchBarMotion';

// macOS shows ⌘, everything else Ctrl. Computed once; SSR-safe — with no
// `navigator` this defaults to Ctrl, and the label corrects on hydration.
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const SHORTCUT_LABEL = isMac ? '⌘K' : 'Ctrl K';

// Matches the old bar exactly, which carried no ⌘K hint. Flip to `true` to show
// the shortcut chip in the header pill (the palette morph handles it either way).
const SHOW_SHORTCUT_HINT = false;

const HEADER_PLACEHOLDER = 'Search...';

export function GlobalSearchTrigger() {
  const { enabled, client, collections, loading } = useGlobalSearchEnabled();
  const [open, setOpen] = useState(false);
  // Query text is persisted per-tab (survives reload + in-tab nav) and scoped on
  // identity so it can't bleed across a sign-out / org-switch (TAS-3542).
  const { query, setQuery } = usePersistedSearchQuery();
  const reduceMotion = useReducedMotion();
  const morphTransition = reduceMotion ? SEARCH_BAR_NO_MOTION : SEARCH_BAR_TRANSITION;

  // ⌘K / Ctrl+K toggles the palette. Always bound so it opens even when search is unavailable.
  // Hand-rolled rather than tinykeys: a bare window listener fires while the user is typing in ANY field (tinykeys' default `ignore` drops exactly those), and it accepts BOTH ⌘ and Ctrl on every platform, where tinykeys' `$mod` narrows to one modifier per OS.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((isOpen) => !isOpen);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // No key and not mid-mint → search is unavailable; the palette says so.
  const unavailable = !enabled && !loading;

  // An org switch / token refresh flips `enabled` false mid-session. Close any
  // open palette so it can't silently re-open (over an unrelated page) once a new
  // key mints and `enabled` returns true.
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  return (
    <>
      {/* Base search bar — a real <input>.
          Rendered only while the palette is closed: on open it unmounts and the
          palette's input row (same `layoutId`) takes over, so `motion` morphs the
          bar into the modal. Opens on explicit pointer press (onPointerDown covers
          mouse + touch + pen), NOT on focus, so pre-typing (e.g. Playwright fill)
          seeds text without opening. Keyboard users open via the input's Enter /
          ArrowDown handler below — focus alone must never open. */}
      {!open && (
        <motion.div
          layoutId={SEARCH_BAR_LAYOUT_ID}
          layout
          transition={morphTransition}
          onPointerDown={(event) => {
            // preventDefault avoids a focus flash before the palette takes over.
            event.preventDefault();
            setOpen(true);
          }}
          className="inline-flex h-[32px] w-full max-w-[300px] items-center gap-1.5 rounded-md border border-neutral-5 bg-white px-2.5 text-14 transition-colors hover:border-neutral-6 focus-within:border-brand-6 focus-within:ring-2 focus-within:ring-ring-primary"
        >
          <motion.span
            layoutId={SEARCH_ICON_LAYOUT_ID}
            layout
            transition={morphTransition}
            className="flex shrink-0 items-center text-neutral-alpha-8"
          >
            <RiSearchLine className="h-4 w-4" aria-hidden />
          </motion.span>
          <input
            type="text"
            aria-label="Search"
            aria-keyshortcuts="Meta+K Control+K"
            placeholder={HEADER_PLACEHOLDER}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === 'ArrowDown') {
                event.preventDefault();
                setOpen(true);
              }
            }}
            className="min-w-0 flex-1 border-0 bg-transparent text-14 text-neutral-text outline-none placeholder:text-neutral-alpha-7"
          />
          {SHOW_SHORTCUT_HINT && (
            <kbd className="rounded-[5px] border border-neutral-5 bg-white px-[5px] py-[1px] text-12 text-neutral-alpha-8">
              {SHORTCUT_LABEL}
            </kbd>
          )}
        </motion.div>
      )}

      <CommandPalette
        open={open}
        onClose={() => setOpen(false)}
        client={client}
        collections={collections}
        unavailable={unavailable}
        minting={loading}
        query={query}
        onQueryChange={setQuery}
        morphTransition={morphTransition}
      />
    </>
  );
}
